<template lang="pug">
.ethic.ethic-page.module-layout
	form#ethic-form.ethic-form(@submit.prevent="submit")
		slot(name="introduction-text")
		fieldset
			fieldset(aria-labelledby="ethic-report-type-label")
				.form-row
					.form-col.form-col--464
						label#ethic-report-type-label(aria-hidden="true") {{ t('label.report_type') }}
					.form-col.form-col--400
						VueRadioGroup#ethic-report-type-radio-group(v-model="model.reportType", :options="getReportTypes", :has-error="hasError('reportType')", :aria-describedby="hasError('reportType') ? 'ethic-report-type-radio-group-error' : false", @blur="validateField('reportType')")
						VueErrorMessage#ethic-report-type-radio-group-error(v-if="hasError('reportType')") {{ getErrorMessage('reportType') }}
			fieldset(aria-labelledby="ethic-subsidiary-type-label")
				.form-row(v-if="isSubsidiary")
					.form-col.form-col--464
						label#ethic-subsidiary-type-label(aria-hidden="true") {{ t('label.subsidiary_type') }}
							VueTooltip(placement="top")
								span(slot="content") {{ t('tooltip.subsidiary_type') }}
					.form-col.form-col--400
						VueRadioGroup#ethic-subsidiary-type-radio-group(v-model="model.subsidiaryType", :options="getSubsidiaryTypes", :has-error="hasError('subsidiaryType')", :aria-describedby="hasError('subsidiaryType') ? 'ethic-subsidiary-type-radio-group-error' : false", @blur="validateField('subsidiaryType')")
						VueErrorMessage#ethic-subsidiary-type-radio-group-error(v-if="hasError('subsidiaryType')") {{ getErrorMessage('subsidiaryType') }}
			.form-row(v-if="isSubsidiary && filteredSubsidiaries && filteredSubsidiaries.length")
				.form-col.form-col--464
					label#ethic-subsidiary-label(for="ethic-subsidiary-select") {{ model.subsidiaryType === getSubsidiaryTypes[0].key ? t('label.subsidiary.domestic') : t('label.subsidiary.foreign') }}
				.form-col.form-col--400
					VueSelect#ethic-subsidiary-select(label-id="ethic-subsidiary-label", v-model="model.subsidiary", :has-error="hasError('subsidiary')", :aria-describedby="hasError('subsidiary') ? 'ethic-subsidiary-select-error' : false", @blur="validateField('subsidiary')")
						VueSelectOption(v-for="(subsidiary, index) in filteredSubsidiaries", label-id="ethic-subsidiary-label", :text="subsidiary.name", :key="subsidiary.id", :index="index", :value="subsidiary.id")
					VueErrorMessage#ethic-subsidiary-select-error(v-if="hasError('subsidiary')") {{ getErrorMessage('subsidiary') }}
			.form-row.form-row--text
				.form-col
					.label-tooltip-wrapper(role="presentation")
						label#ethic-text-label(for="ethic-text-textarea") {{ t('label.text', {max: MAX_TEXT_CHARACTERS}) }}
							VueTooltip(placement="top")
								span(slot="content") {{ t('tooltip.text') }}
				.form-col
					VueTextarea#ethic-text-textarea(v-model="model.text", :maxLength="MAX_TEXT_CHARACTERS", :hasCounter="true", rows="100", :height="300", :has-error="hasError('text')", :aria-describedby="hasError('text') ? 'ethic-text-textarea-error' : false", @blur="validateField('text')")
					VueErrorMessage#ethic-text-textarea-error(v-if="hasError('text')") {{ getErrorMessage('text') }}
			.form-row
				.form-col.form-col--464
					label#ethic-reporter-relation-label(for="ethic-reporter-relation-select") {{ t('label.reporter_relation') }}
				.form-col.form-col--400
					VueSelect#ethic-reporter-relation-select(label-id="ethic-reporter-relation-label", v-model="model.reporterRelation", :max-width="224", :has-error="hasError('reporterRelation')", :aria-describedby="hasError('reporterRelation') ? 'ethic-reporter-relation-select-error' : false", @blur="validateField('reporterRelation')", aria-controls="reporterRelationOther")
						VueSelectOption(v-for="(relationType, index) in getReporterRelationTypes", label-id="ethic-reporter-relation-label", :text="relationType.value", :key="relationType.key", :index="index", :value="relationType.key")
					VueErrorMessage#ethic-reporter-relation-select-error(v-if="hasError('reporterRelation')") {{ getErrorMessage('reporterRelation') }}
			.form-row#reporterRelationOther(v-if="isReporterRelationOther", role="region", aria-live="polite")
				.form-col.form-col--464
					label#ethic-reporter-relation-other-label.sr-only(for="ethic-reporter-relation-other-input") {{ t('list.reporter_relation_types.other') }}
				.form-col.form-col--400
					VueInput#ethic-reporter-relation-other-input(v-model="model.reporterRelationOther", :maxLength="50", :max-width="352", :has-error="hasError('reporterRelationOther')", :aria-describedby="hasError('reporterRelationOther') ? 'ethic-reporter-relation-other-input-error' : false", @blur="validateField('reporterRelationOther')")
					VueErrorMessage#ethic-reporter-relation-other-input-error(v-if="hasError('reporterRelationOther')") {{ getErrorMessage('reporterRelationOther') }}
			fieldset(aria-labelledby="ethic-anonimity-label")
				.form-row
					.form-col.form-col--464
						label#ethic-anonimity-label(aria-hidden="true") {{ t('label.anonimity') }}
					.form-col.form-col--400
						VueRadioGroup#ethic-anonimity-radio-group.ethic-anonimity-radio-group(v-model="model.anonimity", :options="getAnonimityOptions", :has-error="hasError('anonimity')", :aria-describedby="hasError('anonimity') ? 'ethic-anonimity-radio-group-error' : false", @blur="validateField('anonimity')", :aria-controls="model.anonimity ? 'anonimityInfo' : isNotAnonymous ? 'nonAnonimityForm' : null")
						VueErrorMessage#ethic-anonimity-radio-group-error(v-if="hasError('anonimity')") {{ getErrorMessage('anonimity') }}
			VueFormNotification#anonimityInfo(v-if="model.anonimity", type="warning", icon-visible="both") {{ t('brief.insufficient_info') }}
			.dropdown-field(v-if="isNotAnonymous")
				fieldset#nonAnonimityForm(aria-labelledby="nonAnonimityFormText")
					p#nonAnonimityFormText.sf-text(aria-hidden="true")
						strong {{ t('label.anonimity_data_text') }}
					.form-row
						.form-col
							label#ethic-name-label(for="ethic-name-input") {{ t('label.name') }}
						.form-col
							VueInput#ethic-name-input(v-model="model.name", :maxLength="50", :max-width="352", :has-error="hasError('name')", :aria-describedby="hasError('name') ? 'ethic-name-input-error' : false", @blur="validateField('name')")
							VueErrorMessage#ethic-name-input-error(v-if="hasError('name')") {{ getErrorMessage('name') }}
					.form-row
						.form-col
							label#ethic-email-label(for="ethic-email-input") {{ t('label.email') }}
						.form-col
							VueInput#ethic-email-input(v-model="model.email", :maxLength="50",:max-width="352", :has-error="hasError('email')", :aria-describedby="hasError('email') ? 'ethic-email-input-error' : false", @blur="validateField('email')")
							VueErrorMessage#ethic-email-input-error(v-if="hasError('email')") {{ getErrorMessage('email') }}
					.form-row
						.form-col
							label#ethic-address-label(for="ethic-address-input") {{ t('label.address') }}
						.form-col
							VueInput#ethic-address-input(v-model="model.address", :maxLength="50", :max-width="352", :has-error="hasError('address')", :aria-describedby="hasError('address') ? 'ethic-address-input-error' : false", @blur="validateField('address')")
							VueErrorMessage#ethic-address-input-error(v-if="hasError('address')") {{ getErrorMessage('address') }}
					.form-row
						.form-col
							label#ethic-phone-number-label(for="ethic-phone-number-input") {{ t('label.phone_number') }}
						.form-col
							VueInput#ethic-phone-number-input(v-model="model.phoneNumber", :maxLength="50", :max-width="352", :has-error="hasError('phoneNumber')", :aria-describedby="hasError('phoneNumber') ? 'ethic-phone-number-input-error' : false", @blur="validateField('phoneNumber')")
							VueErrorMessage#ethic-phone-number-input-error(v-if="hasError('phoneNumber')") {{ getErrorMessage('phoneNumber') }}
			.form-row.form-row--file-upload
				.file-upload__collapse-item(:class="{'is-active': fileUploadOpened}")
					vue-button#file-upload__toggle-button(button-type="link-dropdown", aria-controls="file-upload__toggle-body", aria-haspopup="true", :aria-expanded="fileUploadOpened.toString()", type="button", @click.prevent="fileUploadOpened = !fileUploadOpened") {{ t('file_upload.title') }}
					#file-upload__toggle-body.file-upload__toggle-body(:aria-expanded="fileUploadOpened.toString()", :aria-hidden="(!fileUploadOpened).toString()", aria-labelledby="file-upload__toggle-button", v-show="fileUploadOpened")
						.file-upload__toggle-body-content
							.ethic-file-upload__info(aria-live="polite")
								svg(xmlns="http://www.w3.org/2000/svg", width="24", height="24", viewBox="0 0 24 24", fill="none", aria-hidden="true")
									path(d="M15 16.2495V18.4995C15 19.3279 14.3284 19.9995 13.5 19.9995H10.5V19.9995C9.67157 19.9995 9 19.3279 9 18.4995V16.2495", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M12 0.999512V2.24951", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M2 10.9995H3", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M4 3.99951L4.5 4.49951", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M22 10.9995H21", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M20 3.99951L19.5 4.49951", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M9.25797 15.9995H14.742C16.7631 15.0257 18.0296 13.013 17.9995 10.823C17.9411 7.63055 15.289 5.05616 12 4.99951C8.70967 5.05621 6.05694 7.63265 6.00048 10.8265C5.97169 13.0152 7.23805 15.0263 9.25797 15.9995Z", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
									path(d="M12 19.9995V22.2495", stroke="#6E7482", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
								p.sf-text-lead {{ t('file_upload.requirements.title') }}
								ul.sf-list.sf-list--empty
									li.sf-listitem {{ t('file_upload.requirements.extensions', {extensions: acceptedFileFormatsText}) }}
									li.sf-listitem {{ t('file_upload.requirements.size', { size: maxFileSize }) }}
									li.sf-listitem {{ t('file_upload.requirements.count', { count: maxFilesCount }) }}
							.ethic-file-upload__overlay-error(v-if="showUploadedFilesCountError", ref="fileUploadErrorMessage", :tabindex="showUploadedFilesCountError ? '-1' : false")
								svg(xmlns="http://www.w3.org/2000/svg", width="24", height="24", viewBox="0 0 24 24", fill="none", aria-hidden="true")
									path(d="M12 15.75V8.25", stroke="#FF435A", stroke-width="2", stroke-linecap="round")
									path(d="M12 18C12.6904 18 13.25 18.5596 13.25 19.25C13.25 19.9404 12.6904 20.5 12 20.5C11.3096 20.5 10.75 19.9404 10.75 19.25C10.75 18.5596 11.3096 18 12 18Z", fill="#FF435A")
									path(d="M13.5851 1.98771V1.98771C13.1551 1.11236 12.097 0.75131 11.2216 1.18128C10.871 1.3535 10.5874 1.63711 10.4152 1.98771L1.15287 20.8567V20.8567C0.790531 21.5938 1.09433 22.4851 1.83143 22.8475C2.03588 22.948 2.26068 23.0002 2.4885 23H21.5118V23C22.3332 23.0005 22.9995 22.3351 23 21.5138C23.0002 21.286 22.948 21.0612 22.8475 20.8567L13.5851 1.98771Z", stroke="#FF435A", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
								p.ethic-file-upload__overlay-error-text(v-html="t('file_upload.errors.count', {count: maxFilesCount})")
							.ethic-file-upload(v-if="uploadedFiles.length === 0")
								VueFileUpload(v-model="files", multiple="true", :accept="acceptedFileFormats", :fileSize="maxFileSize + 'mb'", :fileSizeErrorText="t('file_upload.errors.size', { size: maxFileSize })", :fileTypeErrorText="t('file_upload.errors.extension', {extensions: acceptedFileFormatsText})")
									template(v-slot:tablet-text)
										span(v-html="t('file_upload.upload')")
									template(v-slot:mobile-text)
										span(v-html="t('file_upload.mobile_upload')")
							.ethic-file-upload__overlay.ethic-file-upload__overlay--uploaded(v-if="uploadedFiles.length > 0", v-for="(file, index) in uploadedFiles", :key="file.filename")
								template(v-if="!file.errors.length")
									svg(xmlns="http://www.w3.org/2000/svg", width="42", height="46", viewBox="0 0 42 46", fill="none", aria-hidden="true")
										path(d="M30.9996 45C36.5225 45 40.9996 40.5228 40.9996 35C40.9996 29.4772 36.5225 25 30.9996 25C25.4768 25 20.9996 29.4772 20.9996 35C20.9996 40.5228 25.4768 45 30.9996 45Z", stroke="#52AE30", stroke-width="2")
										path(d="M20.9996 45H6.99963C3.68593 45 0.999634 42.3234 0.999634 39.0097C0.999634 30.5731 0.999634 15.5962 0.999634 10", stroke="#52AE30", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
										path(d="M36.9996 22V10L27.9996 1H7.99963C6.89506 1 5.99963 1.89543 5.99963 3V38C5.99963 39.1046 6.89506 40 7.99963 40H17.4996", stroke="#52AE30", stroke-width="2", stroke-linecap="round", stroke-linejoin="round")
										path(d="M29.9996 38.0001L33.9993 32.0001", stroke="#52AE30", stroke-width="2", stroke-linecap="round")
										path(d="M27 36.0001L29.732 38.0001", stroke="#52AE30", stroke-width="2", stroke-linecap="round")
										path(d="M27.9996 10H26.9996C26.9996 10.5523 27.4473 11 27.9996 11V10ZM36.9996 11C37.5519 11 37.9996 10.5523 37.9996 10C37.9996 9.44771 37.5519 9 36.9996 9V11ZM26.9996 1V10H28.9996V1H26.9996ZM27.9996 11H36.9996V9H27.9996V11Z", fill="#52AE30")
									a(:href="`/otpgroup/complaint/file-download/${file.filename}`", target="_blank") {{ file.filename }}
									button.btn.btn-close(type="button", @click.prevent="onDeleteFile(file.filename)")
										svg(xmlns="http://www.w3.org/2000/svg", width="25", height="24", viewBox="0 0 25 24", fill="none", aria-hidden="true")
											path(fill-rule="evenodd", clip-rule="evenodd", d="M12.998 23.25C19.2113 23.25 24.248 18.2132 24.248 12C24.248 5.7868 19.2113 0.75 12.998 0.75C6.78484 0.75 1.74805 5.7868 1.74805 12C1.74805 18.2132 6.78484 23.25 12.998 23.25Z", stroke="#8E939F", stroke-width="1.5", stroke-linecap="round", stroke-linejoin="round")
											path(d="M8.49805 16.5L17.497 7.5", stroke="#8E939F", stroke-width="1.5", stroke-linecap="round", stroke-linejoin="round")
											path(d="M17.4981 16.5L8.49707 7.5", stroke="#8E939F", stroke-width="1.5", stroke-linecap="round", stroke-linejoin="round")
								template(v-else)
									svg(xmlns="http://www.w3.org/2000/svg", width="42", height="46", viewBox="0 0 42 46", fill="none", aria-hidden="true")
										path(stroke-width="2", stroke="#F7465D", d="m30.9996,45c5.5229,0 10,-4.4772 10,-10c0,-5.5228 -4.4771,-10 -10,-10c-5.5228,0 -10,4.4772 -10,10c0,5.5228 4.4772,10 10,10z")
										path(stroke-width="2", stroke-linejoin="round", stroke="#F7465D", stroke-linecap="round", d="m28.21083,32.38559l5.656,5.656m-5.656,0l5.656,-5.656")
										path(stroke-linejoin="round", stroke-linecap="round", stroke-width="2", stroke="#F7465D", d="m20.9996,45l-13.99997,0c-3.3137,0 -6,-2.6766 -6,-5.9903c0,-8.4366 0,-23.4135 0,-29.0097")
										path(stroke-linejoin="round", stroke-linecap="round", stroke-width="2", stroke="#F7465D", d="m36.9996,22l0,-12l-9,-9l-19.99997,0c-1.10457,0 -2,0.89543 -2,2l0,35c0,1.1046 0.89543,2 2,2l9.49997,0")
										path(fill="#F7465D", d="m27.9996,10l-1,0c0,0.5523 0.4477,1 1,1l0,-1zm9,1c0.5523,0 1,-0.4477 1,-1c0,-0.55229 -0.4477,-1 -1,-1l0,2zm-10,-10l0,9l2,0l0,-9l-2,0zm1,10l9,0l0,-2l-9,0l0,2z")
									p {{ file.filename }}
							.ethic-file-upload.ethic-file-upload--again(v-if="uploadedFiles.length > 0 && uploadedFiles.length < maxFilesCount")
								VueFileUpload(v-model="files", multiple="true", :accept="acceptedFileFormats", :fileSize="maxFileSize + 'mb'", :fileSizeErrorText="t('file_upload.errors.size', { size: maxFileSize })", :fileTypeErrorText="t('file_upload.errors.extension', {extensions: acceptedFileFormatsText})")
									template(v-slot:tablet-text)
										span.sf-btn.sf-btn--link(role="button", v-html="t('file_upload.additional_doc')")
									template(v-slot:mobile-text)
										span.sf-btn.sf-btn--link(role="button", v-html="t('file_upload.additional_doc')")
			h3.sf-heading-3 {{ t('brief.report_process_title') }}
			.form-row
				ul.sf-list
					li.sf-listitem(v-html="t('brief.report_process', {reportProcessUrl})")
					li.sf-listitem(v-html="t('brief.reporter_protection', {reporterProtectionUrl})")
			h3.sf-heading-3 {{ t('label.data_handling_accepted.title') }}
			.form-row(v-html="t('label.data_handling_accepted.text')")
			.form-row(v-if="captchaImg")
				.form-col.form-col--464
					label#ethic-captcha-label(for="ethic-captcha-input") {{ t('label.captcha_text') }}
				.form-col
					VueCaptcha#ethic-captcha-input(v-model="model.captchaText", :max-length="5", :img="`data:image/jpeg;base64,${captchaImg}`", :has-error="captchaError || hasError('captchaText')", :aria-describedby="captchaError || hasError('captchaText') ? 'cash-deposit-captcha-error' : null", :max-width="104", @keyup="resetCaptchaError", @blur="validateField('captchaText')")
					VueErrorMessage#ethic-captcha-error(v-if="hasError('captchaText')") {{ getErrorMessage('captchaText') }}
			.form-row.form-row--last
				VueButton#ethic-problem-next-btn(type="submit", :disabled="submitDisabled") {{ t("next") }}
	slot(name="footer-text")
</template>

<script>
import { computed, defineComponent, reactive, onMounted, getCurrentInstance, watchEffect, ref, watch } from '@vue/composition-api';
import { FULLNAME_REGEXP, PHONE_REGEXP } from '../../../common/js/constants/constants';
import { VueSelect, VueSelectOption } from '@ui-common/vue-select';
import VueInput from '@ui-common/vue-input';
import VueButton from '@ui-common/vue-button';
import VueTextarea from '@ui-common/vue-textarea';
import VueRadioGroup from '@ui-common/vue-radio-group';
import VueSearchFiltered from '@ui-common/vue-search-filtered';
import VueCheckbox from '@ui-common/vue-checkbox';
import VueCaptcha from '@ui-common/vue-captcha';
import VueMobilephoneInput from '@ui-common/vue-mobilephone-input';
import VueErrorMessage from '@ui-common/vue-error-message';
import VueFormNotification from '@ui-common/vue-form-notification';
import VueFileUpload from '@ui-common/vue-file-upload';
import { useCaptcha } from '../../../common/js/composables/captcha';
import { email, required, minLength, createI18nMessage } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useJumpToFirstError } from '../../../common/js/composables/jump-to-first-error';
import { useErrorHandlerUtils } from '../../../common/js/composables/error-handler';
import { usePattern } from '../../../common/js/composables/pattern';
import ethicsApi from '../../../api/ethics-api';
import { lang } from '../mixins/lang';
import { useI18n } from 'vue-i18n-composable';
import { useIsMobile } from '../../news/composables/is-mobile';

const MAX_TEXT_CHARACTERS = 5000;

export default defineComponent({
	mixins: [lang],
	components: {
		VueSelect,
		VueSelectOption,
		VueInput,
		VueTextarea,
		VueRadioGroup,
		VueSearchFiltered,
		VueCaptcha,
		VueMobilephoneInput,
		VueCheckbox,
		VueErrorMessage,
		VueFormNotification,
		VueButton,
		VueFileUpload
	},
	props: {
		subsidiaries: {
			type: Object,
			required: true
		},
		enabledFileExtensions: {
			type: Array,
			required: true
		},
		maxFileSize: {
			type: Number,
			required: true
		},
		maxFilesCount: {
			type: Number,
			required: true
		},
		reportProcessUrl: {
			type: String,
			default: 'https://otpbank.hu'
		},
		reporterProtectionUrl: {
			type: String,
			default: 'https://otpbank.hu'
		}
	},
	emits: ['success', 'error'],
	setup(props, { emit }) {
		const { t } = useI18n();
		const { isMobile } = useIsMobile();

		const fileUploadOpened = ref(false);
		const showUploadedFilesCountError = ref(false);
		const fileUploadErrorMessage = ref(null);
		const { captchaError, technicalError, captchaKey, captchaImg, getCaptcha, resetCaptchaError } = useCaptcha(ethicsApi.captcha);
		onMounted(() => getCaptcha());

		const files = ref([]);
		const uploadedFiles = ref([]);
		const model = reactive({
			reportType: null,
			subsidiaryType: null,
			subsidiary: null,
			text: null,
			reporterRelation: null,
			reporterRelationOther: null,
			anonimity: null,
			name: null,
			email: null,
			address: null,
			phoneNumber: null,
			captchaKey: null,
			captchaText: null
		});
		const submitDisabled = ref(false);

		const isSubsidiary = computed(() => {
			return model.reportType === getReportTypes.value[1].key;
		});
		const isReporterRelationOther = computed(() => {
			return model.reporterRelation === getReporterRelationTypes.value[5].key;
		});
		const isNotAnonymous = computed(() => {
			return model.anonimity === false;
		});
		const filteredSubsidiaries = computed(() => {
			return props.subsidiaries[model.subsidiaryType?.toLowerCase()];
		});
		const acceptedFileFormats = computed(() => props.enabledFileExtensions.toString());
		const acceptedFileFormatsText = computed(() => props.enabledFileExtensions.join(', '));

		const getReportTypes = computed(() => [
			{
				key: 'GROUP',
				value: t('list.report_types.group')
			},
			{
				key: 'SUBSIDIARY',
				value: t('list.report_types.subsidiary')
			}
		]);
		const getSubsidiaryTypes = computed(() => [
			{
				key: 'DOMESTIC',
				value: t('list.subsidiary_types.domestic')
			},
			{
				key: 'FOREIGN',
				value: t('list.subsidiary_types.foreign')
			}
		]);
		const getReporterRelationTypes = computed(() => [
			{
				key: 'EMPLOYEE',
				value: t('list.reporter_relation_types.employee')
			},
			{
				key: 'FORMER_EMPLOYEE',
				value: t('list.reporter_relation_types.former_employee')
			},
			{
				key: 'CLIENT',
				value: t('list.reporter_relation_types.client')
			},
			{
				key: 'SUPPLIER',
				value: t('list.reporter_relation_types.supplier')
			},
			{
				key: 'BUSINESS_PARTNER',
				value: t('list.reporter_relation_types.business_partner')
			},
			{
				key: 'OTHER',
				value: t('list.reporter_relation_types.other')
			}
		]);
		const getAnonimityOptions = computed(() => [
			{ key: true, value: t('list.anonimity.yes') },
			{ key: false, value: t('list.anonimity.no') }
		]);

		const { pattern } = usePattern();
		const messagePath = ({ $propertyPath, $validator }) => `validators.${$propertyPath}.${$validator}`;
		const withI18nMessage = createI18nMessage({ t: t, messagePath });
		const personalInfoRules = computed(() => ({
			name: {
				required: withI18nMessage(required),
				pattern: withI18nMessage(pattern(FULLNAME_REGEXP))
			},
			email: {
				required: withI18nMessage(required),
				email: withI18nMessage(email)
			},
			address: {
				required: withI18nMessage(required),
				minLength: withI18nMessage(minLength(5))
			},
			phoneNumber: {
				required: withI18nMessage(required),
				pattern: withI18nMessage(pattern(PHONE_REGEXP))
			}
		}));
		const rules = computed(() => ({
			reportType: {
				required: withI18nMessage(required)
			},
			subsidiaryType: {
				required: isSubsidiary.value ? withI18nMessage(required) : {}
			},
			subsidiary: {
				required: isSubsidiary.value ? withI18nMessage(required) : {}
			},
			text: {
				required: withI18nMessage(required)
			},
			reporterRelation: {
				required: withI18nMessage(required)
			},
			anonimity: {
				required: withI18nMessage(required)
			},
			...(isNotAnonymous.value && personalInfoRules.value),
			captchaText: {
				captcha: captchaImg.value ? withI18nMessage(() => captchaError.value === false) : {},
				required: captchaImg.value ? withI18nMessage(required) : {}
			}
		}));
		const v$ = useVuelidate(rules, model);
		const { hasError, validateField, getErrorMessage } = useErrorHandlerUtils(v$);
		const { jumpToFirstError } = useJumpToFirstError(getCurrentInstance());

		const submit = async () => {
			v$.value.$touch();
			if (v$.value.$invalid) {
				jumpToFirstError();
			} else {
				submitDisabled.value = true;
				const sentModel = { ...model, captchaKey: captchaKey.value };
				try {
					if (!isNotAnonymous.value) {
						delete sentModel.name;
						delete sentModel.email;
						delete sentModel.address;
						delete sentModel.phoneNumber;
					}
					await ethicsApi.finalize(sentModel);
					emit('success');
				} catch (err) {
					if (err?.response?.status == 400 && err.response?.data?.error == 'captcha') {
						captchaError.value = true;
						model.captchaText = null;
					} else {
						console.error(err);
						emit('error');
					}
				} finally {
					submitDisabled.value = false;
				}
			}
		};
		const onDeleteFile = async (filename) => {
			try {
				await ethicsApi.deleteFile(filename);
				const index = uploadedFiles.value.findIndex((file) => file.filename === filename);
				if (index !== -1) {
					uploadedFiles.value.splice(index, 1);
				}
			} catch (err) {
				console.error(err);
				emit('error');
			}
		};

		watch(files, async (newVal) => {
			if (newVal.length < 1) {
				return;
			}
			if (uploadedFiles.value.length >= props.maxFilesCount) {
				showUploadedFilesCountError.value = true;
				return;
			}
			showUploadedFilesCountError.value = false;
			try {
				for (const file of files.value.slice(0, props.maxFilesCount)) {
					const { data } = await ethicsApi.upload(file);
					if (data.errors.length === 0) {
						const index = uploadedFiles.value.findIndex((file) => file.filename === data.filename);
						if (index !== -1) {
							uploadedFiles.value.splice(index, 1);
						}
						uploadedFiles.value.push(data);
					}
				}
			} catch (err) {
				console.error(err);
				emit('error');
			}
		});
		watchEffect(() => {
			if (technicalError.value) {
				emit('error');
			}
		});

		return {
			getReportTypes,
			getSubsidiaryTypes,
			getReporterRelationTypes,
			getAnonimityOptions,
			isMobile,
			t,
			model,
			filteredSubsidiaries,
			submit,
			hasError,
			validateField,
			getErrorMessage,
			isNotAnonymous,
			isSubsidiary,
			isReporterRelationOther,
			captchaError,
			captchaImg,
			getCaptcha,
			resetCaptchaError,
			submitDisabled,
			fileUploadOpened,
			acceptedFileFormats,
			acceptedFileFormatsText,
			showUploadedFilesCountError,
			fileUploadErrorMessage,
			uploadedFiles,
			onDeleteFile,
			MAX_TEXT_CHARACTERS,
			files
		};
	}
});
</script>

<style src="@ui-common/vue-select/dist/vue-select.css"></style>
<style src="@ui-common/vue-input/dist/vue-input.css"></style>
<style src="@ui-common/vue-textarea/dist/vue-textarea.css"></style>
<style src="@ui-common/vue-search-filtered/dist/vue-search-filtered.css"></style>
<style src="@ui-common/vue-file-upload/dist/vue-file-upload.css"></style>
<style src="@ui-common/vue-checkbox/dist/vue-checkbox.css"></style>
<style src="@ui-common/vue-captcha/dist/vue-captcha.css"></style>
<style src="@ui-common/vue-mobilephone-input/dist/vue-mobilephone-input.css"></style>
<style src="@ui-common/vue-error-message/dist/vue-error-message.css"></style>
<style lang="scss" scoped>
@import '@ui-common/sf-style-utils/lib/helpers/_colors.scss';
@import '@ui-common/sf-style-utils/lib/helpers/_queries.scss';
@import '@ui-common/sf-style-utils/lib/helpers/_mixins.scss';
@import '@ui-common/sf-style-utils/lib/helpers/_paragraph-mixins.scss';

.ethic.ethic-page:not(:last-child) {
	margin-bottom: 48px;
}

.ethic .ethic-introduction-text {
	margin-bottom: 32px;
}

.ethic .sf-form-notification {
	margin-bottom: 32px;
}

.ethic .form-row {
	padding-left: 0;
	padding-right: 0;

	@include media('>=tablet') {
		margin-bottom: 40px;
	}
}

.ethic .form-row:not(.form-row--text) .form-col:first-of-type {
	@include media('>=tablet') {
		align-items: center;
		display: flex;
		min-height: 48px;
	}
}

.ethic .form-row .sf-list:last-of-type,
.ethic .form-row ::v-deep .sf-text:last-child {
	margin-bottom: 0;
}

.ethic .form-col.form-col--400 {
	max-width: 400px;
}

.ethic .form-col.form-col--264 {
	max-width: 264px;
}

.ethic .form-col.form-col--464 {
	max-width: 464px;
}

.ethic .form-col .label-tooltip-wrapper {
	height: auto;
}

.ethic .form-row:last-of-type {
	margin-bottom: 32px;
}

.ethic .sf-radio-group.ethic-anonimity-radio-group ::v-deep .sf-radio-group__pair:nth-child(2):has(input:checked) {
	position: relative;

	&::after {
		bottom: -24px;
		content: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAzMiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiBhcmlhLWhpZGRlbj0idHJ1ZSI+CiAgICA8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTEzLjYyODYgMS45NjM5NUMxMy44ODQ4IDAuODUwNDcgMTQuODU3NCAtMC4wMDA0ODgyODEgMTYgLTAuMDAwNDg4MjgxQzE3LjE0MjYgLTAuMDAwNDg4MjgxIDE4LjExNTIgMC44NTA0NzEgMTguMzcxNCAxLjk2Mzk1QzE5LjM1NDggNi4yMzc3NCAyMi42MjY4IDE1Ljk5OTUgMzIgMTUuOTk5NUgwQzkuMzczMTUgMTUuOTk5NSAxMi42NDUyIDYuMjM3NzMgMTMuNjI4NiAxLjk2Mzk1WiIgZmlsbD0iI2VjZWZmNiI+PC9wYXRoPgo8L3N2Zz4=);
		display: block;
		height: 16px;
		left: -4px;
		position: absolute;
		width: 32px;

		@include media('>=tablet') {
			bottom: -27px;
		}
	}
}

.ethic .dropdown-field {
	@include media('<tablet') {
		margin-left: -24px;
		margin-right: -24px;
		margin-top: -7px;
	}

	@include media('>=tablet') {
		margin-top: -16px;
	}
}

.ethic .dropdown-field .form-row {
	&:last-child {
		margin-bottom: 0;
	}
}

.ethic .dropdown-field .form-row .form-col ::v-deep .sf-error .sf-error__msg {
	background-color: $contentBg-default;
	color: $ui-error;

	&::before {
		background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMTIiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik05LjY4MyAxLjk3QzkuOTE5Ljg2NiAxMC44NyAwIDEyIDBjMS4xMyAwIDIuMDgxLjg2NSAyLjMxNyAxLjk3QzE1LjA0MyA1LjM3NyAxNy4zMTMgMTIgMjQgMTJIMGM2LjY4NyAwIDguOTU4LTYuNjI0IDkuNjgzLTEwLjAzeiIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==);
		bottom: 100%;
	}
}

.ethic .form-row {
	@include media('>=tablet') {
		align-items: baseline;
	}
}

.ethic .form-row--text {
	display: block;

	.form-col {
		max-width: 600px;
	}

	.form-col:last-of-type {
		display: block;
		margin-top: 16px;
		max-width: unset;
	}

	.sf-textarea {
		width: 100%;
	}

	label {
		margin-bottom: 8px;

		@include media('>=desktop') {
			margin-bottom: 16px;
		}
	}
}

.ethic label {
	display: inline-block;
	font-weight: 600;
	height: auto;
	max-width: 296px;
	width: 100%;
}

.ethic .form-col--464 label {
	max-width: 448px;
}

/* FORM-ROW--FILE-UPLOAD */
.ethic .form-row--file-upload .ethic-file-upload__info {
	margin-top: 24px;

	@include media('>=desktop') {
		padding-left: 32px;
		position: relative;
	}

	svg {
		display: none;

		@include media('>=desktop') {
			display: block;
			left: 0;
			position: absolute;
		}
	}

	.sf-list {
		margin-bottom: 0;
	}

	.sf-listitem {
		margin-bottom: 4px;
		padding-left: 16px;
	}
}

.ethic .form-row--file-upload .file-upload__toggle-body {
	background-color: $contentBg-gray-3;
	margin: 16px -24px 0 -24px;
	overflow: hidden;
	padding: 24px;

	@include media('>=tablet') {
		border-radius: 24px;
		margin: 24px 0 0 0;
		padding: 34px;
	}
}

.ethic .form-row--file-upload .file-upload__toggle-body .file-upload__toggle-body-content {
	display: flex;
	flex-direction: column-reverse;
}

.ethic .form-row--file-upload .file-upload__collapse-item::v-deep .sf-btn--link-dropdown::after {
	display: flex;
	flex-grow: 0;
	flex-shrink: 0;
}

.ethic .form-row--file-upload .file-upload__collapse-item.is-active::v-deep .sf-btn--link-dropdown::after {
	transform: translateY(-50%) rotate(225deg);
}

.ethic .form-row--file-upload .ethic-file-upload--again::v-deep .sf-file-upload-box {
	background-color: transparent;
	padding: 0;
}

.ethic .form-row--file-upload .ethic-file-upload--again::v-deep .sf-file-upload-box img {
	display: none;
}

.ethic .form-row--file-upload .ethic-file-upload__overlay--uploaded {
	align-items: center;
	background-color: $contentBg-default;
	border-radius: 16px;
	display: flex;
	gap: 16px;
	height: 100px;
	margin-bottom: 24px;
	padding: 8px 24px;
}

.ethic .form-row--file-upload .ethic-file-upload__overlay .btn-close {
	background-color: transparent;
	cursor: pointer;
	margin-left: auto;

	&:focus {
		outline: $focus-ring solid 2px;
		outline-offset: 3px;
	}
}

.ethic .form-row--file-upload .ethic-file-upload__overlay > svg,
.ethic .form-row--file-upload .ethic-file-upload__overlay > img {
	min-height: 48px;
	min-width: 48px;
}

.ethic-file-upload__overlay.ethic-file-upload__overlay--uploaded ::v-deep a {
	word-break: break-word;
}

.ethic .ethic-form-subtitle {
	margin-bottom: 16px;

	@include media('>=tablet') {
		margin-bottom: 24px;
	}
}
</style>
